import React, { useState, useEffect } from "react";
import { message, Button, Drawer, Space, Popconfirm, Input } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { edit_entity } from "../../services";

import PropTypes from "prop-types";
import { Contacts } from "./components";
import { admitedEntities } from "./utils";
import { CircleAnimationCompact } from "../loading";
import { UserRols } from "../../constants";
const { TextArea } = Input;

const emptyClient = {
	id: "",
	company_id: "",
	entity_company_id: "",
	name: "",
	description: "",
	address: "",
	state: "active",
	contacts: [],
};

export const EditEntityDrawer = ({
	token, 
	user,
	client, 
	openEditClient, 
	onOpenEditClient = () => {},
	onClientToEdit = () => {}, 
	entitySelected
}) => {
	if(!admitedEntities[entitySelected]){
		message.error("Error del Developer: Entidad ingresada en el componente es invalida");
		return <></>;
	}

	const [clientToEdit, setClientToEdit] = useState(emptyClient);

	const { name, description, address, contacts } = clientToEdit; // Values to Edit
	const { id, state, entity_company_tid : clientCompanyTid } = clientToEdit; // Get Values

	const [loading, setLoading] = useState(false);

	const [newCompanyClient, setNewCompanyClient] = useState(null);

	useEffect(() => {
		if(!openEditClient) return;

		setClientToEdit(client);
	}, [openEditClient]);

	const editClient = async() => {
		setLoading(true);

		const data = {
			id,
			name,
			description,
			address,
			contacts,
		};

		const response = await edit_entity(token, "edit", data, entitySelected);

		if(response){
			message.success(`¡${admitedEntities[entitySelected].single} editado con exito!`);
			onClientToEdit(clientToEdit);
			onOpenEditClient(false);

			// Clean Data
			setNewCompanyClient(null);
			setClientToEdit(emptyClient);
		}
		
		setLoading(false);
	};
	
	const archievedClient = async() => {
		setLoading(true);

		const data = { id: clientToEdit.id };
		const response = await edit_entity(token, "archive", data, entitySelected);

		if(response){
			message.success(`¡${admitedEntities[entitySelected].single} archivado con exito!`);
			const archievedClientState = {...client, state: "archieved"};		
			onClientToEdit(archievedClientState);
			onOpenEditClient(false);
		}
		
		setLoading(false);
	};
	const restoreClient = async() => {
		setLoading(true);

		const data = { id: clientToEdit.id };
		const response = await edit_entity(token, "restore", data, entitySelected);

		if(response){
			message.success(`¡${admitedEntities[entitySelected].single} recuperado con exito!`);
			const archievedClientState = {...client, state: "active"};		
			onClientToEdit(archievedClientState);
			onOpenEditClient(false);
		}
		
		setLoading(false);
	};
			
	return (
		<Drawer
			title={`Editar ${admitedEntities[entitySelected].single}`}
			placement="right"
			width={"60vw"}
			onClose={() => {
				setNewCompanyClient(null);
				onOpenEditClient(false);				
			}}
			open={openEditClient}
			extra={
				<Space>
					{
						user.role === UserRols.ARO_ADMIN &&
						<div>
							ID: {clientToEdit.id}
						</div>
					}
					{
						clientToEdit.state !== "archieved" && 
						<Popconfirm
							title={`Archivar ${admitedEntities[entitySelected].single}`}
							description={`Estas seguro que quieres archivar este ${admitedEntities[entitySelected].single}?`}
							onConfirm={archievedClient}
							icon={<QuestionCircleOutlined style={{ color: "red" }} />}
						>
							<Button 
								type="primary"
								loading={loading}
								ghost 
								disabled={state === "archieved"}
							>
								Archivar
							</Button>
						</Popconfirm>
					}
					{
						clientToEdit.state === "archieved" && 
						<Popconfirm
							title={`Recuperar ${admitedEntities[entitySelected].single}`}
							description={`Estas seguro que quieres recuperar este ${admitedEntities[entitySelected].single}?`}
							onConfirm={restoreClient}
							icon={<QuestionCircleOutlined style={{ color: "red" }} />}
						>
							<Button 
								type="primary"
								loading={loading}
								ghost 
								disabled={state === "active"}
							>
								Recuperar
							</Button>
						</Popconfirm>
					}
					
					<Button 
						type="primary" 
						loading={loading}
						onClick={editClient}
						disabled={
							contacts.length === 0 || 
							state === "archieved" || 
							address.trim() === "" || 
							newCompanyClient?.name.trim() === "" ||
							newCompanyClient?.address.trim() === "" ||
							newCompanyClient?.type_tid.trim() === ""
						}
					>
						Editar
					</Button>
				</Space>
			}
		>
			{
				loading && 
				<CircleAnimationCompact/>
			}
			{
				!loading &&
				<>
					<h3>Nombre: </h3>
					<Input 
						placeholder="Inserta un nombre..." 
						value={name}
						onChange={(e) => setClientToEdit({...clientToEdit, name: e.target.value})}
						disabled={state === "archieved"}
					/>
					<h3>Nit Empresa {admitedEntities[entitySelected].single}: {clientCompanyTid}</h3>
					<h3>Descripcion: </h3>
					<TextArea
						value={description}
						placeholder="Ingresa una descripcion..."
						autoSize={{
							minRows: 3,
							maxRows: 5,
						}}
						onChange={(e) => setClientToEdit({...clientToEdit, description: e.target.value})}
						disabled={state === "archieved"}
					/>
					<h3>Direccion: </h3>
					<TextArea
						value={address}
						placeholder="Ingresa una direccion..."
						autoSize={{
							minRows: 3,
							maxRows: 5,
						}}
						onChange={(e) => setClientToEdit({...clientToEdit, address: e.target.value})}
						disabled={state === "archieved"}
					/>
					<h3>Estado: </h3>
					<p>{state}</p>
					<Contacts
						contacts={contacts}
						onChangeContacts={value => setClientToEdit({...clientToEdit, contacts: value})}
						disabled={state === "archieved"}
					/>
				</>
			}
		</Drawer>
	);
};

EditEntityDrawer.propTypes = {
	token: PropTypes.string.isRequired,
	user: PropTypes.object.isRequired,
	client: PropTypes.shape({
		id: PropTypes.string.isRequired,
		company_id: PropTypes.string.isRequired,
		entity_company_id: PropTypes.string.isRequired,
		name: PropTypes.string.isRequired,
		description: PropTypes.string.isRequired,
		state: PropTypes.string.isRequired,
		contacts: PropTypes.array.isRequired,
	}),
	openEditClient: PropTypes.bool.isRequired,
	onOpenEditClient: PropTypes.func,
	onClientToEdit: PropTypes.func,
	entitySelected: PropTypes.string.isRequired,
};